import React, { useMemo, useCallback } from "react"
import { Trans } from "@lingui/macro"
import Config from "react-global-configuration"
import { NoItemsMessage } from ".."
import { KnowledgeActionCard } from "../ActionCards"
import { useAuth } from "../../services"
import { GridArticle, ListArticle, SubmenuArticle, PeekArticle, FullArticle } from "./ArticleListItem"
import { useCategory } from "../../utils/useCategory"

const KnowledgeArticle = ({
  knowledge,
  variant,
  contentTableOfContents,
  onMove,
  onUpdateTableOfContents,
  onArticleChange
}) => {
  const { clientKiosk: kiosk } = Config.get()
  const { hasPermission } = useAuth()
  const { handleEditArticle, handleDeleteArticle, handleCopyArticle } = useCategory()

  const { groups, locations } = knowledge
  const hasGroupsOrLocations = Boolean(groups?.length) || Boolean(locations?.length)

  const scrollTo = useCallback((event, to) => {
    event.preventDefault()

    // Try multiple strategies to find the target element
    const targetElement = document.getElementById(to)

    if (!targetElement) return

    const headerElement = document.querySelector("header")
    const offset = headerElement ? headerElement.clientHeight + 20 : 20
    const pos = targetElement.getBoundingClientRect().top + document.documentElement.scrollTop

    window.scrollTo({
      top: pos - offset,
      behavior: "smooth"
    })
  }, [])

  const hasRestrictedToKiosk = knowledge.isRestrictedToKiosk && !kiosk && !hasPermission("knowledge_update_all")

  const articleContent = useMemo(() => {
    const restrictedMessage = (
      <NoItemsMessage>
        <Trans>
          This article is restricted to kiosk devices for your permission level.
          <br />
          Please access it on a kiosk device to view.
        </Trans>
      </NoItemsMessage>
    )

    switch (variant) {
      case "submenu":
        return <SubmenuArticle scrollTo={scrollTo} contentTableOfContents={contentTableOfContents} />

      case "peek":
        return (
          <PeekArticle
            knowledge={knowledge}
            hasRestrictedToKiosk={hasRestrictedToKiosk}
            onArticleChange={onArticleChange}
          />
        )

      case "full":
        return (
          <FullArticle
            knowledge={knowledge}
            hasRestrictedToKiosk={hasRestrictedToKiosk}
            restrictedMessage={restrictedMessage}
            scrollTo={scrollTo}
            onEdit={handleEditArticle}
            onDelete={handleDeleteArticle}
            onCopy={handleCopyArticle}
            onMove={onMove}
            hasGroupsOrLocations={hasGroupsOrLocations}
            contentTableOfContents={contentTableOfContents}
            onUpdateTableOfContents={onUpdateTableOfContents}
          />
        )

      case "cardlink":
        return <KnowledgeActionCard knowledge={knowledge} />

      case "grid":
        return (
          <GridArticle
            knowledge={knowledge}
            onEdit={handleEditArticle}
            onDelete={handleDeleteArticle}
            onCopy={handleCopyArticle}
            onMove={onMove}
          />
        )

      default:
        return (
          <ListArticle
            hasGroupsOrLocations={hasGroupsOrLocations}
            knowledge={knowledge}
            onEdit={handleEditArticle}
            onDelete={handleDeleteArticle}
            onCopy={handleCopyArticle}
            onMove={onMove}
          />
        )
    }
  }, [
    variant,
    knowledge,
    scrollTo,
    hasRestrictedToKiosk,
    handleEditArticle,
    handleDeleteArticle,
    handleCopyArticle,
    hasGroupsOrLocations,
    onMove,
    contentTableOfContents,
    onUpdateTableOfContents,
    onArticleChange
  ])

  return articleContent
}

export { KnowledgeArticle }
